import { useI18n } from '@affine/i18n';
import type { FC } from 'react';
import { useMemo } from 'react';

import type { ContentDbColumn } from '../utils/get-content-db-columns';
import { partitionByCompatibility } from './compatibility-partition';
import * as css from './controls.css';

export interface ColumnPickerProps {
  value: string | null;
  onChange: (columnId: string | null) => void;
  columns: ReadonlyArray<ContentDbColumn>;
  acceptedTypes?: ReadonlyArray<string>;
  required?: boolean;
  label: string;
  description?: string;
  disabled?: boolean;
  'data-sl-field': string;
}

export const ColumnPicker: FC<ColumnPickerProps> = ({
  value,
  onChange,
  columns,
  acceptedTypes,
  required,
  label,
  description,
  disabled,
  'data-sl-field': field,
}) => {
  const t = useI18n();
  const partitioned = useMemo(
    () => partitionByCompatibility(columns, acceptedTypes),
    [columns, acceptedTypes]
  );
  const noColumns = columns.length === 0;

  return (
    <div className={css.controlRow} data-sl-field={field}>
      <span className={css.controlLabel}>{label}</span>
      {description ? (
        <span className={css.controlDescription}>{description}</span>
      ) : null}
      <select
        className={css.select}
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={disabled || noColumns}
        data-sl-control="column-picker"
      >
        {!required ? (
          <option value="">
            {t['com.streamlain.customApps.viewConfig.column.none']()}
          </option>
        ) : null}
        {required && value === null ? (
          <option value="" disabled>
            {t['com.streamlain.customApps.viewConfig.column.choose']()}
          </option>
        ) : null}
        {partitioned.compatible.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.compatible'
            ]()}
          >
            {partitioned.compatible.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
        {partitioned.castable.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.castable'
            ]()}
          >
            {partitioned.castable.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
        {partitioned.incompatible.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.incompatible'
            ]()}
          >
            {partitioned.incompatible.map(c => (
              <option key={c.id} value={c.id} disabled>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      {noColumns ? (
        <span className={css.disabledHint}>
          {t['com.streamlain.customApps.viewConfig.column.noContentDb']()}
        </span>
      ) : null}
    </div>
  );
};
